/**
 * Contained (cell-anchored) element geometry — PURE logic for F-30/F-32/F-36
 * (no React, no DOM, no Yjs). The DECIDED MODEL (2026-07-14, docs/04 §2 +
 * docs/02 F-30/F-32/F-36):
 *
 *  - A cell-anchored element stores `anchor {kind:'cell',rowId,colId,dx,dy}`,
 *    where `dx`/`dy` is the element's board-unit offset from the cell's
 *    top-left. Its ABSOLUTE board position is derived at render time from the
 *    resolved cell rect + `(dx, dy)` — NOT from the element's `x`/`y` keys,
 *    which are only a fallback cache.
 *  - The cell GROWS so the element is never clipped (F-26 extended): a column
 *    widens to contain `max(dx+w)` up to `CONTAINED_IMAGE_MAX_W`; a cell's
 *    height floor is `max(dy+h)` over the elements it contains (the row then
 *    grows to its tallest cell).
 *
 * No spatial cycle: a cell's SIZE derives from its element extents (dx+w /
 * dy+h); a cell's POSITION derives from cumulative row/col sizes; an element's
 * absolute position = cellPos + (dx, dy). Size never feeds back into offset, so
 * a single layout pass resolves everything (docs/04 §5).
 *
 * The cell math mirrors comments/anchoring.cellRectFromLayout and the F-30
 * drop targeting (core/table.cellAtPosition) so element placement, comment
 * hit-testing and image-drop targeting all agree exactly.
 */
import { type Anchor, type ElementInfo, type ElementType } from "@kola/shared";

import { cellAtPosition } from "../../core/table.js";
import type { BoardRect } from "../../registries/toolbar-actions/index.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";

// F-26 cell-growth extents live in @kola/shared as the SINGLE source of truth
// (the grid render path and the shared per-column helper both consume this, so
// the 640 cap is never re-implemented). Re-exported here for the elements
// feature's existing import sites (GridTable, tests).
export { containedGridExtents, type ContainedGridExtents } from "@kola/shared";

/**
 * Minimum inset (board px) of a freshly-dropped contained element from its
 * cell's top-left — keeps the image off the exact corner so the cell border
 * stays visible. Drags may push the offset back to 0 (flush to the corner).
 */
export const MIN_CONTAINED_OFFSET = 2;

/**
 * Does a cell-anchored element of this type CONFINE to / grow its cell?
 *
 * `true` (image, link-card, sticky, text) — the F-26/F-36 contained model: the
 * cell sizes to fit it (`fitCell` on drop, ratchet on create) and its width is
 * clamped to the cell so it never spills into a neighbour. `false` (drawing) — the element is
 * cell-anchored only to FOLLOW its start cell through sorts/inserts; a freehand
 * stroke overflows cell lines freely (that is the whole point — "belongs to
 * wherever it started" but may be drawn across boundaries), so it is never
 * width-clamped and never ratchets the cell. Callers (contained rect resolution
 * + the drag-commit ratchet in `ElementsLayer`) branch on this so the two rules
 * stay in one place.
 */
export function elementConfinesToCell(type: ElementType): boolean {
  return type !== "drawing";
}

/* ------------------------------------------------------------------ */
/* Cell rect from the grid's published layout                          */
/* ------------------------------------------------------------------ */

/**
 * Board-space rect of a cell in the grid's CURRENT layout, or null when the
 * row/column id is gone or its edge isn't measured yet. Mirrors
 * comments/anchoring.cellRectFromLayout (kept local so the elements feature
 * does not depend on the comments feature — the dependency runs the other way,
 * comments anchor onto elements).
 */
function cellRectFromLayout(
  layout: GridLayoutSnapshot,
  rowId: string,
  colId: string,
): BoardRect | null {
  // O(1) via the published index maps (see GridLayoutSnapshot.rowIndex): every
  // element resolves its rect on every ElementsLayer render, which includes
  // every peer awareness tick and every zoom frame.
  const r = layout.rowIndex.get(rowId);
  const c = layout.colIndex.get(colId);
  if (r === undefined || c === undefined) return null;
  const left = layout.colEdges[c];
  const right = layout.colEdges[c + 1];
  const top = layout.rowEdges[r];
  const bottom = layout.rowEdges[r + 1];
  if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
    return null;
  }
  return { x: layout.originX + left, y: layout.originY + top, w: right - left, h: bottom - top };
}

/**
 * Absolute board rect of a cell-anchored element = resolved cell rect +
 * `(dx, dy)`, at the element's own `h` and a width CONFINED to the column
 * (F-26/F-36). Null when the layout is unavailable or the containing cell
 * can't be resolved this pass (grid not mounted, row/column deleted, edge not
 * measured) — callers fall back to the element's `x`/`y` cache.
 *
 * Width confinement (docs/02 F-26/F-36): the column grows to contain the
 * element up to `CONTAINED_IMAGE_MAX_W`, then "the element confines to the
 * column width" — its right edge is clamped to the cell's right edge so a
 * migrated/oversized image never spills into the neighbouring column's cells.
 * Height is uncapped (rows grow freely), so only `w` is clamped; the clamp is
 * a no-op in the common case where the column grew to fit `dx+w` (cell width ≥
 * dx+w), biting only past the cap. `x`/`dx` are untouched (confinement never
 * moves the element's top-left, so the drag/resize offset math stays exact).
 *
 * `confineWidth` is false for a non-confining type (a drawing —
 * `elementConfinesToCell`): the width clamp is skipped so a freehand stroke can
 * cross cell lines while staying anchored to its start cell, and `anchor.dx`/`dy`
 * may be NEGATIVE (the stroke extends above/left of the cell's top-left).
 */
export function containedElementRect(
  anchor: Extract<Anchor, { kind: "cell" }>,
  w: number,
  h: number,
  layout: GridLayoutSnapshot | null,
  confineWidth = true,
): BoardRect | null {
  if (layout === null) return null;
  const cell = cellRectFromLayout(layout, anchor.rowId, anchor.colId);
  if (cell === null) return null;
  const x = cell.x + anchor.dx;
  if (!confineWidth) return { x, y: cell.y + anchor.dy, w, h };
  const maxW = Math.max(0, cell.x + cell.w - x);
  return { x, y: cell.y + anchor.dy, w: Math.min(w, maxW), h };
}

/**
 * Resolve an element to its absolute board rect for THIS render pass. A
 * cell-anchored element derives from the layout; a board-anchored element (or
 * a contained one whose cell can't be resolved) uses its `x`/`y` cache. A
 * drawing (non-confining type) is not width-clamped to its cell.
 */
export function resolveElementRect(
  element: Pick<ElementInfo, "x" | "y" | "w" | "h" | "anchor" | "type">,
  layout: GridLayoutSnapshot | null,
): BoardRect {
  if (element.anchor.kind === "cell") {
    const rect = containedElementRect(
      element.anchor,
      element.w,
      element.h,
      layout,
      elementConfinesToCell(element.type),
    );
    if (rect !== null) return rect;
  }
  return { x: element.x, y: element.y, w: element.w, h: element.h };
}

/**
 * Elements in paint order with their absolute rects resolved — the input for
 * comment-drop hit-testing (docs/04 §3), so a comment dropped over a contained
 * image anchors to the ELEMENT (not the cell beneath it). Order is preserved
 * (callers pass `board.listElements()`, already (z, id) paint order).
 *
 * `grabRects` applies the SAME live-grab substitution as `elementRectLookup`
 * (F-46/C9): while a peer drags an element, its awareness-streamed preview rect
 * stands in for the committed doc geometry. Passing it is what keeps a hit-test
 * honest — the pins layer POSITIONS pins through the grab-aware lookup, so a
 * hit-test that read only doc geometry would answer about where an element USED
 * to be while the user aims at where it is visibly drawn. F-52b's whole promise
 * is "the prospective target is highlighted so it's unambiguous"; the highlight
 * and the drop must resolve from one set of rects.
 */
export function resolvedElementRects(
  elements: readonly ElementInfo[],
  layout: GridLayoutSnapshot | null,
  grabRects?: ReadonlyMap<string, BoardRect>,
): Array<{ id: string } & BoardRect> {
  return elements.map((el) => ({
    id: el.id,
    ...(grabRects?.get(el.id) ?? resolveElementRect(el, layout)),
  }));
}

/* ------------------------------------------------------------------ */
/* Re-anchoring (F-32/F-36 drag + resize)                              */
/* ------------------------------------------------------------------ */

/**
 * The anchor an element takes after a MOVE (F-32/F-36, amended 2026-08-30).
 *
 * EVERY item that still sits on the table belongs to exactly ONE cell — even
 * one that visibly spans several. Which cell is decided in this order:
 *
 *  1. the cell under the POINTER — where the user actually dropped it. That is
 *     the intent-bearing point, and it is already how the other two ways an
 *     item enters a cell decide: the F-30 image drop homes by the cursor's
 *     cell, and a pen stroke (F-32) by the cell its first point landed in.
 *     A move used to be the odd one out, homing by the dropped TOP-LEFT — so
 *     an item straddling two cells belonged to whichever one its corner fell
 *     in, not the one the user was pointing at.
 *  2. the element's dropped top-left — the old rule, and the only one a
 *     follower of a group drag can use (it has no pointer of its own).
 *  3. the same point CLAMPED into the table, when neither landed on the grid
 *     but the element still OVERLAPS it — a wide item parked half off the
 *     edge. Without this an item plainly sitting on the table would belong to
 *     no cell at all, which is exactly what this rule forbids.
 *  4. board-anchored, only once the element is clear of the table.
 */
export function reanchorForDrop(
  rect: BoardRect,
  pointer: { x: number; y: number } | null,
  layout: GridLayoutSnapshot | null,
): Anchor {
  const board: Anchor = { kind: "board", x: rect.x, y: rect.y };
  if (layout === null) return board;
  const topLeft = { x: rect.x, y: rect.y };
  // (1) the pointer, then (2) the top-left. A follower of a group drag has no
  // pointer of its own and starts at (2).
  if (pointer !== null) {
    const byPointer = cellAnchorAt(pointer, rect, layout);
    if (byPointer !== null) return byPointer;
  }
  const byCorner = cellAnchorAt(topLeft, rect, layout);
  if (byCorner !== null) return byCorner;
  // (3) neither point landed on the grid, but the element itself may still be
  // sitting on it — pull the drop point into the part of the table the element
  // covers and let the cell there own it.
  const onTable = clampToTable(pointer ?? topLeft, rect, layout);
  if (onTable !== null) {
    const byOverlap = cellAnchorAt(onTable, rect, layout);
    if (byOverlap !== null) return byOverlap;
  }
  // (4) genuinely clear of the table.
  return board;
}

/**
 * The cell anchor `rect` takes when `pt` (absolute board units) lands on a
 * cell, else null.
 *
 * The offset is the element's TRUE distance from that cell's top-left and is
 * deliberately NOT clamped to ≥ 0: the cell is now chosen by where the user
 * pointed, not by which cell happens to contain the corner, so clamping would
 * TELEPORT the element to the cell's corner on release — a drop must never
 * move what it homes. A negative offset is already the contained model's norm
 * (`containedElementRect` documents it for a stroke crossing its cell's
 * lines), and F-26 growth reads `dx + w` / `dy + h`, so a negative one simply
 * asks the cell to grow less.
 */
function cellAnchorAt(
  pt: { x: number; y: number },
  rect: BoardRect,
  layout: GridLayoutSnapshot,
): Anchor | null {
  const hit = cellAtPosition(
    layout.colEdges,
    layout.rowEdges,
    pt.x - layout.originX,
    pt.y - layout.originY,
  );
  if (hit === null) return null;
  const rowId = layout.rowIds[hit.row];
  const colId = layout.colIds[hit.col];
  if (rowId === undefined || colId === undefined) return null;
  return {
    kind: "cell",
    rowId,
    colId,
    dx: rect.x - (layout.originX + hit.rect.left),
    dy: rect.y - (layout.originY + hit.rect.top),
  };
}

/**
 * `pt` pulled into the region where the dropped `rect` still overlaps the
 * table, or null when the element is clear of it. The inset keeps the clamped
 * point strictly INSIDE the last row/column — the table's right and bottom
 * edges are exclusive, and a point exactly on one resolves to no cell.
 */
function clampToTable(
  pt: { x: number; y: number },
  rect: BoardRect,
  layout: GridLayoutSnapshot,
): { x: number; y: number } | null {
  const left = layout.originX + (layout.colEdges[0] ?? 0);
  const right = layout.originX + (layout.colEdges[layout.colEdges.length - 1] ?? 0);
  const top = layout.originY + (layout.rowEdges[0] ?? 0);
  const bottom = layout.originY + (layout.rowEdges[layout.rowEdges.length - 1] ?? 0);
  const x0 = Math.max(left, rect.x);
  const x1 = Math.min(right, rect.x + rect.w);
  const y0 = Math.max(top, rect.y);
  const y1 = Math.min(bottom, rect.y + rect.h);
  if (x0 >= x1 || y0 >= y1) return null;
  const inset = 0.5;
  return {
    x: Math.min(Math.max(pt.x, x0), Math.max(x0, x1 - inset)),
    y: Math.min(Math.max(pt.y, y0), Math.max(y0, y1 - inset)),
  };
}

/**
 * The anchor a CONTAINED element keeps after a RESIZE whose top-left moved to
 * `(x, y)`: the SAME cell (a resize RE-GROWS its containing cell per F-32 — it
 * must never re-home the element to a neighbouring cell just because an N/W/S
 * corner dragged the top-left across a boundary, the way a move does), with the
 * offset recomputed from the cell's current top-left and clamped to ≥ 0. An SE
 * resize leaves the top-left unchanged, so `dx`/`dy` are preserved.
 * `cellTopLeftX`/`cellTopLeftY` is the containing cell's board-space top-left
 * (derived by the caller from the element's rect-at-drag-start minus its old
 * `dx`/`dy`).
 */
export function reoffsetWithinCell(
  anchor: Extract<Anchor, { kind: "cell" }>,
  cellTopLeftX: number,
  cellTopLeftY: number,
  x: number,
  y: number,
): Extract<Anchor, { kind: "cell" }> {
  return {
    kind: "cell",
    rowId: anchor.rowId,
    colId: anchor.colId,
    dx: Math.max(0, x - cellTopLeftX),
    dy: Math.max(0, y - cellTopLeftY),
  };
}

/**
 * True when a cell-anchored element's target cell POSITIVELY no longer exists
 * (its row or column was deleted) — the C4/F-36 degradation trigger, mirror of
 * comments' `anchorTargetGone`. Conservative like the comment rule: with NO
 * published layout the cell is merely UNKNOWN (grid not mounted yet), not
 * provably gone, so it returns false — degrading then would strand every
 * contained element on open. A row present in `rowIds` whose edge isn't
 * measured yet is likewise NOT gone (only a missing id counts).
 */
export function containedCellGone(
  anchor: Extract<Anchor, { kind: "cell" }>,
  layout: GridLayoutSnapshot | null,
): boolean {
  if (layout === null) return false;
  return !layout.rowIndex.has(anchor.rowId) || !layout.colIndex.has(anchor.colId);
}

/**
 * The cell anchor for an image dropped over a cell (F-30): `(inCellX, inCellY)`
 * is the pointer's board-unit offset within the cell; both offsets are clamped
 * to at least `MIN_CONTAINED_OFFSET` so the image never sits flush in the
 * corner.
 */
export function containedDropAnchor(
  rowId: string,
  colId: string,
  inCellX: number,
  inCellY: number,
): Extract<Anchor, { kind: "cell" }> {
  return {
    kind: "cell",
    rowId,
    colId,
    dx: Math.max(MIN_CONTAINED_OFFSET, inCellX),
    dy: Math.max(MIN_CONTAINED_OFFSET, inCellY),
  };
}

/** Structural equality of two anchors (skips a redundant LWW anchor write). */
export function anchorsEqual(a: Anchor, b: Anchor): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "board":
      return b.kind === "board" && a.x === b.x && a.y === b.y;
    case "cell":
      return (
        b.kind === "cell" &&
        a.rowId === b.rowId &&
        a.colId === b.colId &&
        a.dx === b.dx &&
        a.dy === b.dy
      );
    case "element":
      return b.kind === "element" && a.elementId === b.elementId && a.dx === b.dx && a.dy === b.dy;
  }
}

